/* Customer Portal — a minimal, customer-facing view of the ticket system.
 *
 * Rationale: previously the customer notification emails linked to /tickets
 * which is the internal admin 3-pane inbox. Customers should see a simple
 * "My Support Requests" portal with no sidebar, no admin chrome, no jargon.
 *
 * Renders OUTSIDE the main Layout wrapper (App.js).
 * Layout: sticky top bar (logo + user + logout) + main content area that
 * switches between list-of-tickets and single-ticket-detail based on route.
 */
import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  LogOut, Plus, Send, ArrowLeft, MessageSquareText, Ticket as TicketIcon,
  AlertCircle, Clock, CheckCircle2, User,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { MyDocuments } from "@/components/portal/MyDocuments";

const PRIORITY_STYLES = {
  Low:     "bg-slate-100 text-slate-700",
  Medium:  "bg-blue-100 text-blue-700",
  High:    "bg-amber-100 text-amber-700",
  Urgent:  "bg-red-100 text-red-700",
};
const STATUS_STYLES = {
  New:                    { icon: AlertCircle, cls: "bg-blue-100 text-blue-700",     label: "New" },
  Open:                   { icon: AlertCircle, cls: "bg-blue-100 text-blue-700",     label: "Open" },
  "In Progress":          { icon: Clock,       cls: "bg-amber-100 text-amber-700",   label: "In Progress" },
  "Waiting on Customer":  { icon: Clock,       cls: "bg-purple-100 text-purple-700", label: "Waiting on you" },
  Resolved:               { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-700", label: "Resolved" },
  Closed:                 { icon: CheckCircle2, cls: "bg-slate-200 text-slate-700",     label: "Closed" },
};

function PortalShell({ children }) {
  const { user, logout } = useAuth();
  const initials = (user?.full_name || user?.email || "?").slice(0, 2).toUpperCase();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-portal">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <NavLink to="/portal/tickets" className="flex items-center gap-2.5">
            <img src="/brand/logo-transparent.png" alt="Jero-One" className="w-9 h-9 object-contain" />
            <div className="leading-tight">
              <div className="text-slate-900 font-bold text-sm">Jero-One Support</div>
              <div className="text-slate-500 text-[10px] uppercase tracking-wider">Customer Portal</div>
            </div>
          </NavLink>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 pr-2">
              <div className="w-8 h-8 rounded-full bg-[var(--green-700)] text-white flex items-center justify-center text-[11px] font-bold">
                {initials}
              </div>
              <div className="leading-tight text-right">
                <div className="text-xs font-semibold text-slate-900 truncate max-w-[140px]">
                  {user?.full_name || user?.email}
                </div>
                <div className="text-[10px] text-slate-500 truncate max-w-[140px]">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              className="text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
              data-testid="portal-logout"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <nav className="flex items-center gap-1 mb-5 bg-white border border-slate-200 rounded-lg p-1 w-fit" data-testid="portal-tabs">
          <NavLink
            to="/portal/tickets"
            data-testid="portal-tab-tickets"
            className={({ isActive }) =>
              `text-xs font-semibold px-3 py-1.5 rounded-md ${isActive ? "bg-[var(--green-700,#0e6939)] text-white" : "text-slate-600 hover:bg-slate-50"}`
            }
          >
            Support Requests
          </NavLink>
          <NavLink
            to="/portal/documents"
            data-testid="portal-tab-documents"
            className={({ isActive }) =>
              `text-xs font-semibold px-3 py-1.5 rounded-md ${isActive ? "bg-[var(--green-700,#0e6939)] text-white" : "text-slate-600 hover:bg-slate-50"}`
            }
          >
            My Documents
          </NavLink>
        </nav>
        {children}
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { icon: AlertCircle, cls: "bg-slate-100 text-slate-700", label: status };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>
      <Icon size={11} />
      {s.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function CustomerPortal({ view = "tickets" }) {
  const { id } = useParams();
  return (
    <PortalShell>
      {view === "documents" ? <MyDocuments /> : id ? <TicketDetail ticketId={id} /> : <TicketList />}
    </PortalShell>
  );
}

function TicketList() {
  const { toast } = useToast();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", priority: "Medium" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTickets(await api.listTickets());
    } catch {
      toast("Could not load your tickets", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.title.trim() || !draft.description.trim()) {
      toast("Please add a subject and description", "error");
      return;
    }
    try {
      const t = await api.createTicket({
        title: draft.title.trim(),
        description: draft.description.trim(),
        priority: draft.priority,
        channel: "portal",
      });
      toast(`Ticket ${t.serial} raised`, "success");
      setDraft({ title: "", description: "", priority: "Medium" });
      setCreating(false);
      nav(`/portal/tickets/${t.id}`);
    } catch (e) {
      // Backend validation errors come back as an array of dicts — flatten to string
      const detail = e?.response?.data?.detail;
      let msg = "Could not raise your request";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(", ");
      else if (detail) msg = JSON.stringify(detail);
      toast(msg, "error");
    }
  };

  return (
    <>
      <div className="flex items-start justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Support Requests</h1>
          <p className="text-sm text-slate-500 mt-1">
            Raise a new issue or track the ones we&apos;re already working on.
          </p>
        </div>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="btn btn-primary shrink-0"
            data-testid="portal-new-ticket"
          >
            <Plus size={14} /> New request
          </button>
        )}
      </div>

      {creating && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 mb-6 space-y-3" data-testid="portal-new-form">
          <div className="grid sm:grid-cols-[1fr_140px] gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase">Subject</label>
              <input
                data-testid="portal-new-title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. Printer at reception not scanning"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase">Priority</label>
              <select
                data-testid="portal-new-priority"
                value={draft.priority}
                onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))}
                className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm"
              >
                {["Low", "Normal", "High", "Urgent"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase">Describe the issue</label>
            <textarea
              data-testid="portal-new-description"
              rows={5}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm resize-y"
              placeholder="What&apos;s happening? Include any error messages, when it started, and what you&apos;ve already tried."
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={() => { setCreating(false); setDraft({ title: "", description: "", priority: "Normal" }); }} className="btn btn-ghost btn-sm">
              Cancel
            </button>
            <button onClick={create} className="btn btn-primary btn-sm" data-testid="portal-new-submit">
              <Send size={13} /> Send request
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-16 text-center">Loading your requests…</div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center">
          <TicketIcon size={36} className="mx-auto text-slate-300 mb-3" />
          <h3 className="font-semibold text-slate-900 mb-1">No requests yet</h3>
          <p className="text-sm text-slate-500 mb-4">
            When you raise a support request, it&apos;ll appear here so you can track our progress.
          </p>
          {!creating && (
            <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
              <Plus size={14} /> Raise your first request
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <button
              key={t.id}
              data-testid={`portal-ticket-row-${t.id}`}
              onClick={() => nav(`/portal/tickets/${t.id}`)}
              className="block w-full text-left bg-white rounded-xl border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow transition p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] font-bold text-slate-500">{t.serial}</span>
                    <StatusBadge status={t.status} />
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY_STYLES[t.priority] || "bg-slate-100"}`}>
                      {t.priority}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-base mt-1 break-words">{t.title}</h3>
                </div>
              </div>
              <p className="text-xs text-slate-500 line-clamp-2">{t.description}</p>
              <div className="text-[11px] text-slate-400 mt-2">
                Raised {formatDate(t.created_at)} · {(t.comments || []).length} update{((t.comments || []).length !== 1) ? "s" : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function TicketDetail({ ticketId }) {
  const { toast } = useToast();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTicket(await api.getTicket(ticketId));
    } catch {
      toast("Could not load this request. It may have been removed or you don't have access.", "error");
      nav("/portal/tickets");
    } finally {
      setLoading(false);
    }
  }, [ticketId, toast, nav]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      // Customers can only post public replies (backend forces this via role)
      await api.addTicketComment(ticketId, { body: reply.trim(), public: true });
      setReply("");
      toast("Reply sent", "success");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = "Could not send reply";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(", ");
      else if (detail) msg = JSON.stringify(detail);
      toast(msg, "error");
    } finally {
      setSending(false);
    }
  };

  if (loading || !ticket) {
    return <div className="text-sm text-slate-500 py-16 text-center">Loading…</div>;
  }

  return (
    <>
      <button
        onClick={() => nav("/portal/tickets")}
        className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 mb-4"
        data-testid="portal-back-to-list"
      >
        <ArrowLeft size={14} /> All my requests
      </button>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 mb-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-mono text-xs font-bold text-slate-500">{ticket.serial}</span>
          <StatusBadge status={ticket.status} />
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY_STYLES[ticket.priority] || "bg-slate-100"}`}>
            {ticket.priority}
          </span>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">{ticket.title}</h1>
        <div className="text-xs text-slate-500 mt-2">Raised on {formatDate(ticket.created_at)}</div>
      </div>

      {/* Thread */}
      <div className="space-y-3">
        {/* Original request */}
        <MessageBubble
          author={ticket.client_contact_person || ticket.client_name || "You"}
          date={ticket.created_at}
          body={ticket.description}
          mine
        />

        {/* Public comments only — customers must not see internal notes */}
        {(ticket.comments || [])
          .filter((c) => c.public)
          .map((c, i) => (
            <MessageBubble
              key={c.id || `${c.created_at || ""}-${i}`}
              author={c.author_name || "Jero-One Support"}
              date={c.created_at}
              body={c.body}
              mine={false}
            />
          ))}
      </div>

      {/* Reply composer — only if ticket is still open */}
      {["Resolved", "Closed"].includes(ticket.status) ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-6 text-center">
          <CheckCircle2 size={20} className="mx-auto text-emerald-600 mb-1" />
          <div className="text-sm font-semibold text-emerald-800">This request has been {ticket.status.toLowerCase()}.</div>
          <div className="text-xs text-emerald-700 mt-1">
            If you&apos;re still experiencing issues, please raise a new request.
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mt-6" data-testid="portal-reply-composer">
          <label className="text-xs font-semibold text-slate-700 uppercase flex items-center gap-1.5">
            <MessageSquareText size={13} /> Add a reply
          </label>
          <textarea
            data-testid="portal-reply-body"
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to our support team…"
            className="w-full mt-2 border border-slate-200 rounded-md px-3 py-2 text-sm resize-y"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={send}
              disabled={!reply.trim() || sending}
              className="btn btn-primary btn-sm"
              data-testid="portal-reply-send"
            >
              <Send size={13} /> {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ author, date, body, mine }) {
  const initials = (author || "?").slice(0, 2).toUpperCase();
  return (
    <div className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
        mine ? "bg-slate-200 text-slate-700" : "bg-[var(--green-700)] text-white"
      }`}>
        {mine ? <User size={14} /> : initials}
      </div>
      <div className={`min-w-0 flex-1 ${mine ? "text-right" : ""}`}>
        <div className={`text-xs text-slate-500 mb-1 ${mine ? "" : "flex items-center gap-2"}`}>
          <b>{mine ? "You" : author}</b> · {formatDate(date)}
        </div>
        <div className={`inline-block max-w-full text-left rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap break-words ${
          mine ? "bg-slate-100 text-slate-800" : "bg-white border border-slate-200 text-slate-800"
        }`}>
          {body || "(no content)"}
        </div>
      </div>
    </div>
  );
}
